import React from 'react';

/**
 * Utilities
*/
import { createWorkReport, getWorkReport, updateWorkReport } from '../../utils/api';
import { convertBlobUrlToFile, convertFileToBase64 } from '../../utils/image';
import { workHasBuiltInType } from '../../utils/misc';
import { storeReportPayload, getStoredReportPayload } from '../../utils/reports';

/**
 * MUI Components
 */
import { Tabs, Tab, Button, Box, Paper, CircularProgress, Typography } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import BuildIcon from '@mui/icons-material/Build';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ForestIcon from '@mui/icons-material/Forest';

/**
 * Components
 */
import { TabContent1, TabContent2, TabContent3, TabContent4, TabContent5 } from '../../components/Reports/AddReportTabs';
import ConfirmationDialog from '../../components/Dialogs/ConfirmationDialog';

/**
 * Hooks
 */
import { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppMenu } from '../../contexts/NavBarContext';
import useToken from '../../hooks/useToken';
import { useNetworkState } from "@uidotdev/usehooks";

/**
 * Contexts
 */
import { ReportFormProvider, ReportFormContext } from '../../contexts/ReportFormContext';
import { SharedFormProvider } from '../../contexts/SharedFormContext';
import { useNotification } from '../../contexts/NotificationContext';

const tabStyles = {
    fontSize: '0.5rem',
    '@media (max-width:440px)': {
        minWidth: '50px !important',
    },
};

const styles = {
    container: {
        width: '100%',
        typography: 'body1',
        position: 'relative'
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        pointerEvents: 'none',
    },
    loader: {
        textAlign: 'center',
        pointerEvents: 'auto',
    },
}

/**
 * Check if the report data is valid
 * @param {object} report the report object
 * @param {function} t the translation function
 * @returns {boolean} true if the report is valid, false otherwise
 */
const checkIsValid = (report, t) => {

    // Require photos notes.
    // Must be set on the component prop as well!
    const requirePhotosNotes = true;

    // Initialize the response object
    let response = {
        isValid: false,
        errors: [],
    };

    // If there is no report, abort!
    if (!report) {
        console.warn('Cannot check report validity. Invalid report!', report);
        response.errors.push(t('Invalid report'));
        return response;
    }

    // Check if the report has content
    const fieldsToCheck = ['consumptions', 'employees', 'photos', 'vehicles'];
    response.isValid = fieldsToCheck.some(
        key => Array.isArray(report[key]) && report[key].length > 0
    );

    // Check that active vehicles have km or hours of use
    if (response.isValid && Array.isArray(report.vehicles)) {
        report.vehicles
            .filter(v => v.checked === true || v.not_from_planning === true)
            .forEach(v => {
                const km = Number(v.current_kilometers) || 0;
                const hours = Number(v.hours_of_use) || 0;
                if (km <= 0 && hours <= 0) {
                    const code = v.code || v.vehicle?.code || '';
                    response.errors.push(
                        code
                            ? t('Vehicle usage required for', { code })
                            : t('Vehicle usage required')
                    );
                    response.isValid = false;
                }
            });
        if (!response.isValid) return response;
    }

    // Check if all photos have notes when required
    if (Array.isArray(report.photos)
        && report.photos.length > 0
        && requirePhotosNotes) {
        const photosWithNotes = report.photos.filter(photo => photo.notes?.trim());
        if (photosWithNotes.length !== report.photos.length) {
            response.errors.push(t('All photos must have a note'));
            response.isValid = false;
            return response;
        }
    }

    return response;
};

/**
 * Work report page content
 * 
 * @returns {JSX.Element}
 */
const WorkReportContent = ({ edit = false, old = null }) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const [activeTab, setActiveTab] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const { state: routerState } = location;
    const { report, setReport, resetReport } = useContext(ReportFormContext);
    const { updateTitle } = useAppMenu();
    const [isValid, setIsValid] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const abortControllerRef = useRef(new AbortController());
    const confirmationDialogRef = useRef();
    const networkState = useNetworkState();
    const { showNotification } = useNotification();

    // Memoize the combined state to prevent infinite loops
    const state = useMemo(() => {
        return routerState || (old ? {
            uid: old?.uid || 0,
            work: old?.work || null,
            planning: old?.planning || null,
        } : null);
    }, [routerState, old?.uid, old?.work, old?.planning]);

    // Setup page title
    let pageTitle = edit ? t('Edit') : t('Do report');
    if (workHasBuiltInType(state?.work || old?.work)) {
        pageTitle += ` | ${state?.work?.work_type?.name || old?.work?.work_type?.name || ''}`;
    }
    updateTitle(pageTitle);

    // Handle navigation tabs
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Handle navigation tabs by swiping
    const handleSwipe = (index) => {
        setActiveTab(index);
    };

    const numberOfTabs = 4;
    const handlers = useSwipeable({
        onSwipedLeft: () => handleSwipe((activeTab + 1) % numberOfTabs),
        onSwipedRight: () => handleSwipe((activeTab - 1 + numberOfTabs) % numberOfTabs),
        preventDefaultTouchmoveEvent: true,
        trackMouse: true
    });

    // On mount
    useEffect(() => {

        // Populate the report data on edit
        if (edit && old) {
            resetReport();

            if (old.vehicles) {
                // Make sure the vehicles have checked property
                old.vehicles.forEach((vehicle) => {
                    vehicle.checked = vehicle.was_used;
                    vehicle.not_used_note = vehicle.notes || '';
                });
            }

            if (old.trailers) {
                // Make sure the trailers have checked property
                old.trailers.forEach((trailer) => {
                    trailer.checked = trailer.was_used;
                    trailer.not_used_note = trailer.notes || '';
                });
            }

            setReport(prevReport => ({ ...prevReport, ...old }));
        }

        // Cleanup function to abort the fetch if the component unmounts
        return () => {
            abortControllerRef.current.abort();
        };
    }, [state, edit, old]);

    // Check form validity
    useEffect(() => {
        const response = checkIsValid(report, t);
        setIsValid(response.isValid);
        setError(response.errors.join('\n'));
        console.debug('Report changed:', report, state);
    }, [report]);

    // Show the Snackbar when there is a new error
    useEffect(() => {
        if (error) {
            setSnackbarOpen(true);
        }
    }, [error]);

    // Warn the user before unloading the page if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (hasUnsavedChanges()) {
                event.preventDefault();
                event.returnValue = ''; // This triggers the browser's default warning
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [report]); // Re-run when report changes

    // Intercept browser back button navigation
    useEffect(() => {
        const handlePopState = (event) => {
            // Check if there are unsaved changes
            if (hasUnsavedChanges()) {
                event.preventDefault();
                event.stopPropagation();
                handleCancel(event);
            } else {
                // Just navigate back
                navigate('/');
            }
        };

        // Listen for browser back button
        window.addEventListener('popstate', handlePopState, { capture: true });

        return () => {
            window.removeEventListener('popstate', handlePopState, { capture: true });
        };
    }, [report]);


    // Generate the report payload for sending/store
    const generatePayload = async () => {
        const payload = {
            ...report,
            uid: state?.uid || old?.uid,
            work_id: state?.work.id || old?.work.id,
            planning_id: state?.planning?.id || old?.planning?.id,
            photos: [],
        };

        // Handle photos
        let photos = report.photos;
        if (photos) {
            // Preserve existing photos by including their ids so the backend does not delete them on update
            (photos || []).forEach((photo) => {
                if (photo.id > 0) {
                    payload.photos.push({ id: photo.id, notes: photo.notes });
                }
            });

            try {
                const photosWithFiles = await Promise.all(photos.map(async photo => {
                    // Skip photos that has been already uploaded.
                    // Like in edits/updates of the report.
                    if (photo.id > 0) {
                        return { ...photo };
                    }

                    // Do needed conversions before submission
                    const file = await convertBlobUrlToFile(photo.imageUrl);
                    const base64 = await convertFileToBase64(file);
                    return { ...photo, file, base64 };
                }));

                photosWithFiles.forEach((photo, index) => {

                    // Skip photos that has been already uploaded.
                    // Like in edits/updates of the report.
                    if (photo.id > 0) {
                        return;
                    }

                    payload.photos.push({
                        index,
                        // file: photo.file,
                        name: photo.file.name,
                        notes: photo.notes,
                        lastModified: photo.file.lastModified,
                        base64: photo.base64
                    });
                });
            } catch (error) {
                console.error('Failed to convert photo to base64:', error);
            }
        }

        return payload;
    };

    // Generate the context for local store
    const generateContext = async () => {
        return {
            work: state?.work || old?.work,
            planning: state?.planning || old?.planning,
        };
    };

    // Handle the close event for the Snackbar
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            // Ignore clickaway action
            return;
        }
        setSnackbarOpen(false);
    };

    // Handle save for offline mode
    const handleSave = async (e) => {
        e.preventDefault();
        document.activeElement?.blur();
        await new Promise((r) => setTimeout(r, 0));
        setIsBusy(true);
        setError(null);

        const payload = await generatePayload();
        const context = await generateContext();

        // Store the report for later
        let key = storeReportPayload(payload, context);
        if (!key) {
            console.error('Failed to store report payload');
            setIsBusy(false);
            setError(t('Failed to store report payload'));
            return;
        }

        // Cleanup form state
        resetReport();
        setIsBusy(false);

        // Go back
        navigate(-1);
    };

    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
        // Check if any of the report arrays have items
        return report.consumptions.length > 0 ||
            report.employees.length > 0 ||
            report.photos.length > 0 ||
            report.vehicles.length > 0 ||
            report.trailers.length > 0 ||
            report.extras.length > 0;
    };

    // Handle the cancel event
    const handleCancel = (e) => {
        e.preventDefault();

        // Check if there are unsaved changes
        if (hasUnsavedChanges()) {
            // Show confirmation dialog
            confirmationDialogRef.current.openDialog({
                title: t('Unsaved Changes'),
                text: t('You have unsaved changes. Are you sure you want to leave? All changes will be lost.'),
                onConfirm: () => {
                    // Reset the form and navigate back
                    resetReport();
                    navigate('/');
                }
            });
        } else {
            // Just navigate back
            navigate('/');
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isBusy) {
            console.warn('Form is busy');
            return;
        }

        document.activeElement?.blur();
        await new Promise((r) => setTimeout(r, 0));

        setIsBusy(true);
        setError(null);

        const payload = await generatePayload();

        if (edit) {
            // UPDATE EXISTING
            try {
                const result = await updateWorkReport(token, payload);
                if (result.message == 'ok') {
                    // Cleanup form state
                    resetReport();

                    // Notify success to user
                    showNotification(t('Report sent and updated successfully'), 'success');

                    // Go back
                    navigate(-1);
                } else {
                    console.log('Unknown response:', result);
                }
            } catch (error) {
                setError(error.message);
            } finally {
                setIsBusy(false);
            }
        } else {
            // CREATE NEW
            try {
                const result = await createWorkReport(token, payload);
                if (result.message == 'ok') {
                    // Cleanup form state
                    resetReport();

                    // Notify success to user
                    showNotification(t('Report sent successfully'), 'success');

                    // Go back
                    navigate(-1);
                } else {
                    console.log('Unknown response:', result);
                }
            } catch (error) {
                setError(error.message);
            } finally {
                setIsBusy(false);
            }
        }
    };

    // Helper function to check if the work has a specific built-in type
    const isWorkType = (builtInKey) => {
        let theKey = state?.work?.work_type?.builtin_key || old?.work?.work_type?.builtin_key;
        return theKey === builtInKey;
    }

    return (
        <Box {...handlers} sx={styles.container}>
            {isBusy && (<Box sx={styles.overlay}>
                <Box className={styles.loader}>
                    <CircularProgress />
                    <Typography variant="h6">{t('Loading')}</Typography>
                </Box>
            </Box>)}
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                aria-label="Work report tabs"
            >
                <Tab icon={<AccessTimeIcon />} label={t('Worker')} sx={tabStyles} />
                <Tab icon={<BuildIcon />} label={t('Consumptions')} sx={tabStyles} />
                <Tab icon={<DirectionsCarIcon />} label={t('Vehicles')} sx={tabStyles} />
                <Tab icon={<PhotoCameraIcon />} label={t('Photos')} sx={tabStyles} />
                {isWorkType('forestal') && <Tab icon={<ForestIcon />} label={t('Extras')} sx={tabStyles} />}
            </Tabs>

            <div>
                <div hidden={activeTab !== 1}>
                    <TabContent1 state={state} />
                </div>
                <div hidden={activeTab !== 0}>
                    <TabContent2 state={state} edit={edit} />
                </div>
                <div hidden={activeTab !== 3}>
                    <TabContent3 />
                </div>
                <div hidden={activeTab !== 2}>
                    <TabContent4 state={state} edit={edit} vehicleCheckDefault={true} />
                </div>
                {isWorkType('forestal') && (
                    <div hidden={activeTab !== 4}>
                        <TabContent5 state={state} />
                    </div>
                )}
            </div>

            <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
                <Box sx={{
                    display: 'flex',
                    gap: '20px',
                    justifyContent: 'space-between',
                    p: 2,
                }}>
                    <Button onClick={handleCancel} disabled={isBusy} variant="contained" color="primary">
                        {t('Cancel')}
                    </Button>
                    {networkState.online && (
                        <Button onClick={handleSubmit} disabled={!isValid || isBusy} variant="contained" color="primary">
                            {edit ? t('Update') : t('Send')}
                        </Button>
                    )}
                    {!networkState.online && (
                        <Button onClick={handleSave} disabled={!isValid || isBusy} variant="contained" color="secondary">
                            {t('Save for later')}
                        </Button>
                    )}
                </Box>
            </Paper>

            {error && (
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={5000}
                    onClose={handleSnackbarClose}
                    message={error}
                />
            )}

            {/* Confirmation Dialog for unsaved changes */}
            <ConfirmationDialog ref={confirmationDialogRef} />

        </Box>
    );
};

/**
 * Work Report Page component. Can handle multiple modes (create,edit,show).
 * @param {object} props Page params { mode = 'create|edit|show' }
 * @returns the component
 */
const WorkReportPage = ({ mode = 'create' }) => {

    const navigate = useNavigate();
    const { id, key } = useParams();
    const { token } = useToken();
    const [reportData, setReportData] = useState(null);

    const isLocal = (id && !key) ? false : true;

    useEffect(() => {
        // Create an AbortController instance
        const controller = new AbortController();
        const { signal } = controller;

        const fetchReportData = async (reportId) => {
            try {
                const result = await getWorkReport(token, reportId, signal, {
                    for_edit: true, // This will include the uid and planning needed for the "state"
                });
                console.debug('fetchReportData', result);
                setReportData(result.report);
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('Fetch aborted');
                } else {
                    console.error(error.message);
                    setReportData(null);
                }
            }
        };

        const fetchLocalReportData = (reportKey) => {

            // FIXME: we need as well the uid and the planning object

            let localReport = getStoredReportPayload(reportKey);
            if (Object.hasOwn(localReport, 'payload')) {
                // Load report data from local storage
                setReportData(localReport.payload);
                console.info('Fetched report payload from local storage:', reportKey);
            } else {
                console.error('Failed to fetch report payload from local storage:', reportKey);
            }
        };

        if (mode === 'edit') {
            if (isLocal) {
                if (!key) {
                    console.error('Cannot open work report edit view. No key passed!');
                    navigate(-1); // Navigate back if there's no key
                } else {
                    // Fetch report data here
                    fetchLocalReportData(key);
                }
            } else {
                if (!id) {
                    console.error('Cannot open work report edit view. No ID passed!');
                    navigate(-1); // Navigate back if there's no ID
                } else {
                    // Fetch report data here
                    fetchReportData(id);
                }
            }

            // Add isEdit flag to the report data
            setReportData(prevReportData => ({ ...prevReportData, isEdit: true }));
        }

        // Cleanup function to abort fetch request on component unmount or mode change
        return () => {
            controller.abort();
        };
    }, [mode, id, key, navigate]);

    const renderContent = () => {
        switch (mode) {
            case 'create':
                return <WorkReportContent />;
            case 'edit':
                if (!reportData) {
                    return <div>Loading report data...</div>;
                }

                if (isLocal) {
                    return <WorkReportContent edit={true} old={reportData} />;
                }

                return <WorkReportContent edit={true} old={reportData} />;
            case 'show':
                return <div>Show Mode: Viewing the report details.</div>;
            default:
                return <div>Invalid mode.</div>;
        }
    };

    return (
        <SharedFormProvider>
            <ReportFormProvider>
                <style>
                    {`
                        #app-bar-menu,
                        #menu-user-Profile {
                            display: none;
                        }
                        #app-bar-pages button {
                            visibility: hidden;
                        }
                    `}
                </style>
                {renderContent()}
            </ReportFormProvider>
        </SharedFormProvider>
    );
};

export default WorkReportPage;
